import * as R from 'remeda';
import { z } from 'zod';
import { SchemaDatapoint } from '@rossum/api-client/schemas';

const VARIABLE_REF = /\$\$([a-zA-Z_][a-zA-Z0-9_]*)/g;

const matchingSchema = z
  .object({
    configuration: z.object({
      queries: z.array(z.unknown()),
      variables: z
        .record(z.object({ __formula: z.string().optional() }))
        .default({}),
    }),
  })
  .partial();

const extractVariablesFromValue = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return R.pipe(
      [...value.matchAll(VARIABLE_REF)].map(m => m[1]),
      R.filter(R.isDefined)
    );
  }

  if (Array.isArray(value)) {
    return value.flatMap(extractVariablesFromValue);
  }

  if (value && typeof value === 'object') {
    return Object.values(value).flatMap(extractVariablesFromValue);
  }

  return [];
};

export const extractVariablesFromQuery = (query: unknown): string[] => {
  return R.pipe(extractVariablesFromValue(query), R.unique());
};

export type LookupVariables = Record<string, { __formula?: string }>;

export type VariablesPerQueryResult = {
  variablesPerQuery: string[][];
  variables: LookupVariables;
};

export const extractVariablesPerQuery = (
  flatSchema: SchemaDatapoint[],
  fieldSchemaId: string
): VariablesPerQueryResult | undefined => {
  const field = flatSchema.find(dp => dp.id === fieldSchemaId);

  const matching = field && 'matching' in field ? field.matching : undefined;

  const parsed = matchingSchema.safeParse(matching);

  if (!parsed.success || !parsed.data.configuration) return undefined;

  return {
    variablesPerQuery: parsed.data.configuration.queries.map((query: unknown) =>
      extractVariablesFromQuery(query)
    ),
    variables: parsed.data.configuration.variables,
  };
};
